import { useState } from 'react'
import {
  Box, Typography, Chip, Collapse, IconButton, Tooltip,
} from '@mui/material'
import { ExpandMore, ExpandLess, Fingerprint, VerifiedUser, Warning } from '@mui/icons-material'
import DriftBadge from './DriftBadge'

interface AgentABFDetailProps {
  abf: any
  fingerprint?: any
  certificate?: any
}

const AgentABFDetail = ({ abf, fingerprint, certificate }: AgentABFDetailProps) => {
  const [expanded, setExpanded] = useState(false)

  if (!abf) return null

  // error_posture_score is deliberately not rendered. It scored an agent by
  // hunting the surrounding file for Python keywords — try/except, retry,
  // timeout — which answers a question about the module rather than about the
  // agent, and returns 0 for every spec-defined agent because there is no file
  // text to search. 201 of 206 production fingerprints were 0, and the UI
  // showed each of them as "0/100 (Weak)": a governance weakness asserted
  // about agents nothing had ever assessed. Failure handling is shown by
  // AgentResilience instead, derived from what the scanner actually reports.

  const certStatus = certificate?.status
  const certColor = certStatus === 'active' ? 'success' : certStatus === 'suspended' ? 'warning' : 'default'

  // Count risk signals
  const risks: string[] = []
  if (abf.tool_permissions_detail?.some((t: any) => t.type === 'code_exec'))
    risks.push('Can execute code')
  if (abf.tool_permissions_detail?.some((t: any) => t.domain === 'external'))
    risks.push('Accesses external services')
  if (abf.memory_access_detail?.cross_agent_readable)
    risks.push('Memory readable by other agents')
  if (abf.orchestration_dependency_detail?.max_depth > 2)
    risks.push(`Deep nesting (depth ${abf.orchestration_dependency_detail.max_depth})`)

  const toolCount = abf.tool_permissions_detail?.length || 0

  return (
    <Box sx={{ mt: 1, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
      {/* Always-visible summary */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
        <Fingerprint sx={{ fontSize: 16, color: 'primary.main' }} />
        <Typography variant="caption" sx={{ fontWeight: 600 }}>Behavioral Fingerprint</Typography>
        {fingerprint?.verdict && <DriftBadge verdict={fingerprint.verdict} size="small" />}
        {certificate && (
          <Tooltip title={`Certificate: ${certStatus} | Expires: ${certificate.expires_at ? new Date(certificate.expires_at).toLocaleDateString() : 'N/A'}`}>
            <Chip icon={<VerifiedUser sx={{ fontSize: 12 }} />} label={certStatus}
              color={certColor as any} size="small" variant="outlined" sx={{ fontSize: '0.6rem', height: 20 }} />
          </Tooltip>
        )}
      </Box>

      {/* Key metrics — always visible */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
        <Tooltip title="How many external tools this agent can invoke">
          <Chip label={`${toolCount} tool${toolCount !== 1 ? 's' : ''} permitted`} size="small" variant="outlined"
            color={toolCount > 5 ? 'warning' : 'default'} sx={{ fontSize: '0.65rem', height: 20 }} />
        </Tooltip>
        {abf.orchestration_dependency_detail?.is_orchestrator && (
          <Chip label="Orchestrator" size="small" color="primary" sx={{ fontSize: '0.65rem', height: 20 }} />
        )}
        {abf.orchestration_dependency_detail?.has_human_in_loop && (
          <Chip label="Human-in-loop" size="small" color="success" sx={{ fontSize: '0.65rem', height: 20 }} />
        )}
      </Box>

      {/* Risk signals — always visible if present */}
      {risks.length > 0 && (
        <Box sx={{ mt: 0.5 }}>
          {risks.map((r, i) => (
            <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
              <Warning sx={{ fontSize: 12, color: 'warning.main' }} />
              <Typography variant="caption" color="warning.main" sx={{ fontSize: '0.65rem' }}>{r}</Typography>
            </Box>
          ))}
        </Box>
      )}

      {/* Goal preview — always visible if present */}
      {abf.goal_text_preview && (
        <Box sx={{ mt: 0.5 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>Instructions: </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            {abf.goal_text_preview.length > 120 ? abf.goal_text_preview.substring(0, 120) + '...' : abf.goal_text_preview}
          </Typography>
        </Box>
      )}

      {/* Expandable details */}
      <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', mt: 0.5 }}
        onClick={() => setExpanded(!expanded)}>
        <IconButton size="small">
          {expanded ? <ExpandLess sx={{ fontSize: 14 }} /> : <ExpandMore sx={{ fontSize: 14 }} />}
        </IconButton>
        <Typography variant="caption" color="text.secondary">
          {expanded ? 'Hide' : 'Show'} full fingerprint details
        </Typography>
      </Box>

      <Collapse in={expanded}>
        <Box sx={{ pl: 2, pt: 0.5, pb: 0.5 }}>
          {/* Tool permissions detail */}
          {abf.tool_permissions_detail?.length > 0 && (
            <Box sx={{ mb: 1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                Tool Permission Breakdown
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.3, mt: 0.3 }}>
                {abf.tool_permissions_detail.map((t: any, i: number) => (
                  <Tooltip key={i} title={`Type: ${t.type} | Domain: ${t.domain} | Scope: ${t.scope}`}>
                    <Chip label={t.name} size="small"
                      color={t.type === 'code_exec' ? 'error' : t.domain === 'external' ? 'warning' : 'default'}
                      variant="outlined" sx={{ fontSize: '0.6rem', height: 20 }} />
                  </Tooltip>
                ))}
              </Box>
            </Box>
          )}

          {/* Memory detail */}
          {abf.memory_access_detail && (
            <Box sx={{ mb: 1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>Memory Access</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.3, mt: 0.3 }}>
                <Chip label={abf.memory_access_detail.type} size="small" variant="outlined" sx={{ fontSize: '0.6rem', height: 20 }} />
                <Chip label={`retention: ${abf.memory_access_detail.retention}`} size="small" variant="outlined" sx={{ fontSize: '0.6rem', height: 20 }} />
                {abf.memory_access_detail.read_sources?.map((s: string) => (
                  <Chip key={s} label={s} size="small" color="info" variant="outlined" sx={{ fontSize: '0.6rem', height: 20 }} />
                ))}
                {abf.memory_access_detail.cross_agent_readable && (
                  <Chip label="cross-agent readable" size="small" color="error" variant="outlined" sx={{ fontSize: '0.6rem', height: 20 }} />
                )}
              </Box>
            </Box>
          )}

          {/* Orchestration detail */}
          {abf.orchestration_dependency_detail && (
            <Box sx={{ mb: 1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>Orchestration</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.3, mt: 0.3 }}>
                <Chip label={`depth: ${abf.orchestration_dependency_detail.max_depth}`} size="small" variant="outlined" sx={{ fontSize: '0.6rem', height: 20 }} />
                <Chip label={`inbound: ${abf.orchestration_dependency_detail.inbound_callers}`} size="small" variant="outlined" sx={{ fontSize: '0.6rem', height: 20 }} />
                {abf.orchestration_dependency_detail.outbound_calls?.map((c: string) => (
                  <Chip key={c} label={`calls: ${c}`} size="small" variant="outlined" sx={{ fontSize: '0.6rem', height: 20 }} />
                ))}
              </Box>
            </Box>
          )}

          {/* Hashes */}
          <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: '0.6rem', color: 'text.disabled' }}>
            goal: {abf.goal_embedding_hash?.substring(0, 16)}... | composite: {abf.composite_hash?.substring(0, 16)}...
          </Typography>
        </Box>
      </Collapse>
    </Box>
  )
}

export default AgentABFDetail
